import * as ProgressPrimitive from "@radix-ui/react-progress"
import * as React from "react"

import { cn } from "@/utils/cn"

type ProgressVariant = "brand" | "success" | "danger" | "warning" | "muted"

type ProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  variant?: ProgressVariant
  animate?: boolean
}

const getVariantGradientClasses = (variant: ProgressVariant): string => {
  switch (variant) {
    case "success":
      return "from-green-500 via-emerald-500 to-emerald-600"
    case "danger":
      return "from-red-500 via-rose-500 to-red-600"
    case "warning":
      return "from-yellow-500 via-amber-500 to-orange-500"
    case "muted":
      return "from-gray-500 via-gray-400 to-gray-500"
    case "brand":
    default:
      return "from-brand-blue via-brand-cyan to-brand-blue-dark"
  }
}

const getVariantGlowTailClass = (variant: ProgressVariant): string => {
  switch (variant) {
    case "success":
      return "to-emerald-400/40"
    case "danger":
      return "to-rose-400/40"
    case "warning":
      return "to-amber-400/40"
    case "muted":
      return "to-gray-400/40"
    case "brand":
    default:
      return "to-brand-blue/40"
  }
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, variant = "brand", animate = true, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      // Track
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary/60 backdrop-blur-xs ring-1 ring-white/10",
      className
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      // Filled bar
      className={cn(
        "relative h-full w-full flex-1 rounded-r-full",
        // Smooth transform animation for value changes
        "transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
      )}
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    >
      {/* Gradient fill */}
      <div className={cn("absolute inset-0 rounded-r-full bg-gradient-to-r", getVariantGradientClasses(variant))} />

      {/* Subtle shimmer sweep across the filled portion */}
      {animate && (
        <div className="absolute inset-0 rounded-r-full opacity-60 shimmer pointer-events-none" />
      )}
    </ProgressPrimitive.Indicator>

    {/* Leading-edge glow anchored to current value */}
    {animate && typeof value === "number" && value > 0 && value < 100 && (
      <div
        className={cn(
          "pointer-events-none absolute top-0 bottom-0 w-8 -translate-x-1/2 bg-gradient-to-r from-transparent blur-md",
          getVariantGlowTailClass(variant)
        )}
        style={{ left: `${value}%` }}
      />
    )}
  </ProgressPrimitive.Root>
))
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
